import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import Modal from '@/assets/common/Modal/Modal.vue';
import { toCamel } from 'snake-camel';
import moment from 'moment';

type MappedDataStock = {
    chemical_id: string | number;
    chemical_name: string;
    amount: string | number;
    inner_capacity:  string | number;
    purchase_date: string;
    quantity:  string | number;
    unit_name:string;
    chemicalErrorFlag : boolean;
    chemicalNameErrorFlag : boolean;
    chemicalDateErrorFlag : boolean;
    chemicalCapacityErrorFlag : boolean;
    chemicalQuanityErrorFlag : boolean;
    chemicalAmountErrorFlag : boolean;
    chemicalIdNameMismatch :boolean;
  }

  type ChemicalStocks = {
    display_name: string;
    id: number;
    unit_name: string;
  }

@Component({
    components: {
        Modal
    }
})
export default class ChemicalBulkInsertModal extends Vue {
    @Prop()
    private value!: boolean

    @Prop()
    private warehouseId!: string

    private csvData: string[][] = [];

    private unMatchedData: string[][] = [];

    private modalTitle = '農薬一括登録'

    private selectedChemicals: ChemicalStocks[] = []


    get showChemicalBulkInsertModal(): boolean {
        return this.value
    }
    set showChemicalBulkInsertModal(b: boolean) {
        b
    }
    private mappedResult: MappedDataStock[] = [];

    private unmatchedChemicalDetailsArray : { id: string | number; unit_name: string }[] = [];

    get baseUrl(): string {
        return `/admin/warehouses/${this.warehouseId}/chemicals/stocks`
    }
     
    @Emit('save')
    save(): void {
        const data = { stocks: this.mappedResult, chemicals:this.unmatchedChemicalDetailsArray};
        
         this.$apiClient.doRequest('put', this.baseUrl, data)
         .then(() => {
             this.$toasted.success('保存しました。')
         })
         .catch(() => {
             this.$toasted.error('保存に失敗しました。')
         })
        this.clearSelectedFile();
        this.mappedResult = [];
    }
    @Emit('cancel')
    cancel(): void {
        const result = window.confirm('変更内容を破棄します。よろしいですか。')
        if (!result) {
            return
        }
        this.clearSelectedFile();
        this.mappedResult = [];
    }

    clearSelectedFile(){
        const fileInput = document.querySelector('.csv-upload-button') as HTMLInputElement | null;
        if (fileInput) {
            fileInput.value = '';
        }
    }

    downloadCSV() {
        const headers = ['登録番号','農薬名','購入日', '内容量','本数','単位','金額'];

        const csvContent = headers.join(',');

        const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: 'text/csv;charset=utf-8' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = '農薬一括登録（フォーマット）.csv';
        link.click();
    }

    uploadCSV(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];

        if (file) {
          const reader = new FileReader();
          reader.onload = () => {
            const csvText = reader.result as string;
            this.parseCSV(csvText);
            input.value = '';
          };
          reader.readAsText(file,'Shift_JIS');
        }
    }

    parseCSV(csvText: string) {
        const rows = csvText.split('\n');
        this.csvData = rows.map(row => row.split(','));    
        if (this.csvData[this.csvData.length - 1].length === 1 && this.csvData[this.csvData.length - 1][0] === '') {
          this.csvData.pop();
        }
        this.validationCheck()
    }

    private validationCheck() {
        this.mappedResult = this.mapToDesiredFormat(this.csvData);
        this.$apiClient.getRequest(`/admin/warehouses/${this.warehouseId}`).then((res) => {
            this.selectedChemicals = toCamel(res.data.chemicals) as ChemicalStocks[];

            const unmatchedChemicalIds = this.mappedResult
            .filter(resultChemical => !this.selectedChemicals.some(chem => chem.id === resultChemical.chemical_id))
            .map(resultChemical => resultChemical.chemical_id);

            this.unmatchedChemicalDetailsArray = this.mappedResult
            .filter(resultChemical => unmatchedChemicalIds.includes(resultChemical.chemical_id))
            .map(resultChemical => ({ id: resultChemical.chemical_id, unit_name: resultChemical.unit_name}));
            
            
            // Check if the chemicals in mappedResult are valid
            this.mappedResult.forEach((resultChemical) => {
                const selectedChemical = this.selectedChemicals.find((chem) => (chem.id) === resultChemical.chemical_id);
                
                if (selectedChemical) {
                    if (selectedChemical.display_name !== resultChemical.chemical_name) {
                        resultChemical.chemicalIdNameMismatch = true;
                    } else {
                        resultChemical.chemicalIdNameMismatch = false;
                    }
                }
          });
        });
    }
       
    private mapToDesiredFormat(data: string[][]): MappedDataStock[] {
        const mappedData: MappedDataStock[] = [];
        for (const row of data.slice(1)) {
            const [chemicalId, pesticideName, purchaseDate, contentAmount, quantity, unit, amount] = row;
        
            // Call the halfWidthConvertion function for each element in the row
            const convertedRow = row.map(item => this.halfWidthConvertion(item));
        
            const [convertedChemicalId, convertedPesticideName, convertedPurchaseDate, convertedContentAmount, convertedQuantity, convertedUnit, convertedAmount] = convertedRow;
        
            const datePattern = /^(\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{8})$/;
            const chemicalErrorFlag: boolean = convertedChemicalId.trim() === '' || !/^\d+$/.test(convertedChemicalId);
            const chemicalNameErrorFlag: boolean = convertedPesticideName.trim() === '';
            const chemicalDateErrorFlag: boolean = convertedPurchaseDate.trim() === '' || !datePattern.test(convertedPurchaseDate.trim());
            const chemicalCapacityErrorFlag: boolean = convertedContentAmount.trim() === '' || !/^\d+$/.test(convertedContentAmount);
            const chemicalQuanityErrorFlag: boolean = convertedQuantity.trim() === '' || !/^\d+$/.test(convertedQuantity);
            const chemicalAmountErrorFlag: boolean = convertedAmount.trim() === '' || !/^\d+$/.test(convertedAmount.trim());
        
            const mappedEntry: MappedDataStock = {
                chemical_id: convertedChemicalId ? parseInt(convertedChemicalId, 10) : "", 
                chemical_name: convertedPesticideName,
                amount: convertedAmount ? parseInt(convertedAmount,10) : "",
                inner_capacity:convertedContentAmount ? parseInt(convertedContentAmount,10) : "",
                purchase_date: moment(new Date(convertedPurchaseDate)).format('yyyy-MM-DD'),
                quantity: convertedQuantity ? parseInt(convertedQuantity,10):"",
                unit_name: convertedUnit,
                chemicalErrorFlag: chemicalErrorFlag,
                chemicalNameErrorFlag: chemicalNameErrorFlag,
                chemicalDateErrorFlag: chemicalDateErrorFlag,
                chemicalCapacityErrorFlag: chemicalCapacityErrorFlag,
                chemicalQuanityErrorFlag: chemicalQuanityErrorFlag,
                chemicalAmountErrorFlag: chemicalAmountErrorFlag,
                chemicalIdNameMismatch: false,
            };
            mappedData.push(mappedEntry);
        }
        return mappedData;
      }

    private halfWidthConvertion(inputString: string): string {
        return inputString
            .split('')
            .map(char => {
                const code = char.charCodeAt(0);
                if (code >= 65281 && code <= 65374) {
                    return String.fromCharCode(code - 65248);
                }
                return char;
            })
        .join('');
    }
 }
